import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Upload, RotateCcw } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const SLOTS = [
  { slot: "cover_front", label: "COGNITO front cover", original: "/covers/cognito-front.jpg" },
  { slot: "cover_back", label: "COGNITO back cover", original: "/covers/cognito-back.jpg" },
  { slot: "echo_front", label: "ECHO cover", original: "/covers/echo-front.jpg" },
  { slot: "timeless_front", label: "TIMELESS cover", original: "/covers/timeless-front.jpg" },
  ...["cognito", "dawn", "evie", "adam", "gage", "mary"].map((id) => ({ slot: `char_${id}`, label: `${id[0].toUpperCase()}${id.slice(1)} portrait`, original: `/characters/${id}.jpg?v=2` })),
];

// Shrink large photos in the browser so uploads stay small and fast.
const shrink = (file, max = 1800) => new Promise((resolve, reject) => {
  const img = new Image();
  img.onload = () => {
    const scale = Math.min(1, max / Math.max(img.width, img.height));
    const c = document.createElement("canvas");
    c.width = Math.round(img.width * scale);
    c.height = Math.round(img.height * scale);
    c.getContext("2d").drawImage(img, 0, 0, c.width, c.height);
    c.toBlob((b) => (b ? resolve(b) : reject(new Error("resize failed"))), "image/jpeg", 0.88);
    URL.revokeObjectURL(img.src);
  };
  img.onerror = reject;
  img.src = URL.createObjectURL(file);
});

const Slot = ({ s, version, authHeaders, onChange }) => {
  const [busy, setBusy] = useState(false);
  const src = version ? `${BACKEND}/api/images/${s.slot}?v=${version}` : s.original;

  const upload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setBusy(true);
    try {
      const blob = await shrink(file);
      const fd = new FormData();
      fd.append("file", blob, `${s.slot}.jpg`);
      const r = await axios.post(`${BACKEND}/api/admin/images/${s.slot}`, fd, authHeaders);
      onChange(s.slot, r.data.version);
      toast.success(`${s.label} updated — live now.`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed.");
    } finally {
      setBusy(false);
    }
  };

  const reset = async () => {
    try {
      await axios.delete(`${BACKEND}/api/admin/images/${s.slot}`, authHeaders);
      onChange(s.slot, null);
      toast.success(`${s.label} restored.`);
    } catch { toast.error("Could not restore."); }
  };

  return (
    <div className="rounded-2xl bg-white/5 border border-white/10 p-3 flex flex-col gap-2" data-testid={`admin-image-${s.slot}`}>
      <img src={src} alt={s.label} className="w-full aspect-[3/4] object-cover rounded-xl bg-black" />
      <p className="text-xs text-gray-300">{s.label}{version && <span className="text-cyan-300"> · custom</span>}</p>
      <label className={`inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-full text-xs cursor-pointer ${busy ? "bg-white/5 text-gray-500" : "bg-cyan-500/15 hover:bg-cyan-500/25 text-cyan-100 border border-cyan-400/40"}`}>
        <Upload className="w-3.5 h-3.5" /> {busy ? "Uploading…" : "Replace"}
        <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={upload} disabled={busy} data-testid={`admin-image-input-${s.slot}`} />
      </label>
      {version && (
        <button type="button" onClick={reset} className="inline-flex items-center justify-center gap-1.5 text-xs text-gray-400 hover:text-white" data-testid={`admin-image-reset-${s.slot}`}>
          <RotateCcw className="w-3 h-3" /> Restore original
        </button>
      )}
    </div>
  );
};

export const ImageSlots = ({ authHeaders }) => {
  const [versions, setVersions] = useState({});
  useEffect(() => {
    axios.get(`${BACKEND}/api/settings`).then((r) => setVersions(r.data?.images || {})).catch(() => {});
  }, []);
  return (
    <div className="glass rounded-3xl border border-white/10 p-6" data-testid="admin-images">
      <h3 className="font-orbitron text-xs uppercase tracking-[0.25em] text-cyan-300 mb-4">Book covers &amp; portraits</h3>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {SLOTS.map((s) => (
          <Slot key={s.slot} s={s} version={versions[s.slot]} authHeaders={authHeaders} onChange={(slot, v) => setVersions((p) => ({ ...p, [slot]: v }))} />
        ))}
      </div>
    </div>
  );
};
